<template>
  <div
    v-bind:class="{
      'vue-code-input-container': true,
      [className]: !!className,
    }"
    v-bind:style="{ width: `${fields * fieldWidth}px` }"
  >
    <p class="title" v-if="title">{{ title }}</p>

    <div class="d-flex">
      <template v-for="(v, index) in values">
        <input
          class="form-control text-center mr-2"
          :type="type === 'number' ? 'tel' : type"
          :pattern="type === 'number' ? '[0-9]' : null"
          :autoFocus="autoFocus && !loading && index === autoFocusIndex"
          :style="{
            width: `${fieldWidth}px`,
            height: `${fieldHeight}px`,
          }"
          :key="`${id}-${index}`"
          :data-id="index"
          :value="v"
          :ref="iRefs[index]"
          @input="onValueChange"
          @focus="onFocus"
          @keydown="onKeyDown"
          :disabled="disabled"
          :required="required"
          maxlength="1"
        />
      </template>
    </div>

    <div
      v-if="loading"
      class="loading"
      :style="{ lineHeight: `${fieldHeight}px` }"
    >
      <div class="blur" />
      <digger-loader small />
    </div>
  </div>
</template>

<script>
/**
 * Original code is from https://github.com/suweya/vue-verification-code-input.
 * I copied instead of installing to have more control in customizing this component.
 */

const KEY_CODE = {
  backspace: 8,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
}

export default {
  name: 'CodeInput',

  props: {
    type: {
      type: String,
      default: 'number',
    },

    className: String,

    fields: {
      type: Number,
      default: 5,
    },

    fieldWidth: {
      type: Number,
      default: 58,
    },

    fieldHeight: {
      type: Number,
      default: 54,
    },

    autoFocus: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    title: String,

    change: Function,

    complete: Function,

    loading: {
      type: Boolean,
      default: false,
    },

    // The autofocus attribute only works on a full page load, not when the
    // page is reached by an in-app redirect, so this focuses it in code.
    focusOnMount: {
      type: Boolean,
      default: false,
    },
  },

  mounted() {
    if (this.focusOnMount) this.focusFirstEmpty()
  },

  data() {
    const { fields, values } = this
    let vals
    let autoFocusIndex = 0

    if (values && values.length) {
      vals = []
      for (let i = 0; i < fields; i++) {
        vals.push(values[i] || '')
      }

      autoFocusIndex = values.length >= fields ? 0 : values.length
    } else {
      vals = Array(fields).fill('')
    }

    this.iRefs = []
    for (let i = 0; i < fields; i++) {
      this.iRefs.push(`input_${i}`)
    }

    this.id = +new Date()
    return { values: vals, autoFocusIndex }
  },

  methods: {
    focusFirstEmpty() {
      this.$nextTick(() => {
        const refs = this.$refs[this.iRefs[this.autoFocusIndex]]
        if (refs && refs[0] && !refs[0].disabled) refs[0].focus()
      })
    },

    onFocus(e) {
      e.target.select(e)
    },

    onValueChange(e) {
      const index = parseInt(e.target.dataset.id)
      const { type, fields } = this

      if (type === 'number') {
        e.target.value = e.target.value.replace(/[^\d]/gi, '')
      }
      // this.handleKeys[index] = false;
      if (
        e.target.value === '' ||
        (type === 'number' && !e.target.validity.valid)
      ) {
        return
      }

      let next
      const value = e.target.value
      let { values } = this
      values = Object.assign([], values)

      if (value.length > 1) {
        let nextIndex = value.length + index - 1
        if (nextIndex >= fields) {
          nextIndex = fields - 1
        }

        next = this.iRefs[nextIndex]
        const split = value.split('')
        split.forEach((item, i) => {
          const cursor = index + i
          if (cursor < fields) {
            values[cursor] = item
          }
        })

        this.values = values
      } else {
        next = this.iRefs[index + 1]
        values[index] = value
        this.values = values
      }

      if (next) {
        const element = this.$refs[next][0]
        element.focus()
        element.select()
      }

      this.triggerChange(values)
    },

    onKeyDown(e) {
      const index = parseInt(e.target.dataset.id)
      const prevIndex = index - 1
      const nextIndex = index + 1
      const prev = this.iRefs[prevIndex]
      const next = this.iRefs[nextIndex]

      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault()
          const vals = [...this.values]

          if (this.values[index]) {
            vals[index] = ''
            this.values = vals
            this.triggerChange(vals)
          } else if (prev) {
            vals[prevIndex] = ''
            this.$refs[prev][0].focus()
            this.values = vals
            this.triggerChange(vals)
          }
          break
        }

        case KEY_CODE.left:
          e.preventDefault()
          if (prev) {
            this.$refs[prev][0].focus()
          }
          break

        case KEY_CODE.right:
          e.preventDefault()
          if (next) {
            this.$refs[next][0].focus()
          }
          break

        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault()
          break

        default:
          // this.handleKeys[index] = true;
          break
      }
    },

    triggerChange(values = this.values) {
      const { fields } = this
      const val = values.join('')
      this.$emit('change', val)

      if (val.length >= fields) {
        this.$emit('complete', val)
      }
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.vue-code-input-container {
  position: relative;
}
.loading {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
}
.blur {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  opacity: 0.5;
  filter: blur(0.5px);
  transition: opacity 0.3s;
}
.title {
  margin: 0;
  height: 20px;
  padding-bottom: 10px;
}

/* Phones: the inline sizes (58px boxes, fields*58 container) overflow narrow
   viewports. Let the container span its parent and the boxes shrink evenly to
   fit, capped at their designed size. !important is required to beat the
   inline styles. */
@media (max-width: 575.98px) {
  .vue-code-input-container {
    width: 100% !important;
    max-width: 100%;
  }

  .vue-code-input-container .d-flex {
    justify-content: center;
  }

  .vue-code-input-container input {
    width: auto !important;
    flex: 1 1 0;
    min-width: 0;
    max-width: 58px;
  }

  .vue-code-input-container input:last-child {
    margin-right: 0 !important;
  }
}
</style>
